import React, {useEffect, useState} from "react";
import {Document, Page, pdfjs} from 'react-pdf';
import {DialogActions, DialogContent, DialogTitle, Fade} from "@mui/material";
import Dialog from "../../base/dialog";
import {Row} from "reactstrap";
import Button from "../../base/button/button";
import Utils, {downloadFile} from "../../../../core/services/utils/utils";
import Iframe from "react-iframe";


/**
 * THE types that will be rendered in the filePreview modal.
 */
export const FilePreviewTypes = {
    pdf: 'pdf',
    video: 'video',
    image: 'image',
    html: 'html',
    unknown: 'unknown',
}


const FilePreviewModal = ({data, open, setOpen, fetchByOpenNewTab}) => {
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [downloadFailed, setDownloadFailed] = useState(false);
    const [downloading, setDownloading] = useState(false);

    /**
     * Resets the state of the dialog after it has been closed
     */
    useEffect(() => {
        if (!open) {
            setNumPages(0)
            setPageNumber(1);
            setDownloading(false);
            setDownloadFailed(false);
        }
    }, [open])

    /**
     * Sets the global worker for the pdfjs as soon as the component mounts.
     */
    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    }, [])

    /**
     * Sets the page numbers and the selected page number as soon as the pdf document loads successfully.
     * @param numPages
     */
    const onDocumentLoadSuccess = ({numPages}) => {
        setNumPages(numPages);
        setPageNumber(1);
    }


    /**
     * Downloads the file of the technical file for the user by fetching the file then downloading it using an <a> tag.
     */
    const _downloadFile = async () => {
        if (fetchByOpenNewTab) {
            window.open(data.url);
            return;
        }
        setDownloadFailed(false);
        setDownloading(true)
        await fetch(data?.url)
            .then(resp => {
                if (resp.status !== 200) return undefined;
                return resp.blob();
            })
            .then(blob => {
                if (!blob) {
                    setDownloadFailed(true);
                    return;
                }
                downloadFile(blob, data?.fileName);
            })
            .catch((e) => {
                setDownloadFailed(true)
            });
        setDownloading(false)
    }

    /**
     * Renders the content of this modal based on the type of the given data.
     * @return {JSX.Element}
     */
    const renderContent = () => {
        switch (data?.type) {
            case FilePreviewTypes.pdf:
                return (
                    <>
                        <Document
                            file={data?.url}
                            onLoadSuccess={onDocumentLoadSuccess}
                            error={
                                <div className={'mb-3 mt-4 py-5 d-flex justify-content-center' +
                                    ' align-items-center text-center'}>
                                    Failed to load this PDF
                                </div>
                            }>
                            <Page pageNumber={pageNumber}/>
                            {
                                numPages > 1 &&
                                <div className={'page-controls'}>
                                    <button
                                        onClick={() => setPageNumber(prevState => Math.max(1, prevState - 1))}>
                                        Prev
                                    </button>
                                    <span>
                                                     {pageNumber ?? 0} of {numPages ?? 0}
                                                </span>
                                    <button
                                        onClick={() => setPageNumber(prevState => Math.min(numPages, prevState + 1))}>
                                        Next
                                    </button>
                                </div>}
                        </Document>

                    </>
                )
            case FilePreviewTypes.video:
                return (
                    <video controls
                           autoPlay
                           className={'d-flex justify-content-center rounded-xxl'}>
                        <source src={Utils.encodeFileUrl(data?.url)} type={`video/mp4`}/>
                    </video>
                )
            case FilePreviewTypes.html:
                return (
                    <Iframe
                        url={data?.url}
                        width={'100%'}
                        height={'100%'}
                        frameBorder={0}
                        className={'preview-iframe'}
                    />
                );
            case FilePreviewTypes.image:
                return (
                    <div className={'d-flex justify-content-center preview-image'}>
                        <img src={Utils.encodeFileUrl(data?.url)} alt={data?.fileName ?? "preview image"}/>
                    </div>
                );
            case FilePreviewTypes.unknown:
            default:
                return (
                    <div className={'d-flex text-center justify-content-center my-3'}>
                        <p className={'text-sm text-color-1 my-5 py-5'}>
                            Unfortunately, preview is unavailable for the specified file. You can still
                            download it though.
                        </p>
                    </div>
                );

        }
    }


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={[FilePreviewTypes.pdf, FilePreviewTypes.html].includes(data?.type)
                ? 'lg'
                : 'md'
            }
            className={'preview-dialog bound-dialog'}>
            <DialogTitle className={'p-3 px-5'}
                         id="scroll-dialog-title">
                <p className={'text-lg text-color-1 font-weight-semi-bold '}>
                    Preview
                </p>
            </DialogTitle>
            <DialogContent dividers={true} className={'py-3'}>
                <Row className={'px-3 w-100 d-flex justify-content-center no-gutters'}>
                    <Fade in={downloadFailed} mountOnEnter unmountOnExit>
                        <div
                            className={'w-100 my-3 text-center align-items-center d-flex justify-content-center' +
                                ' text-sm'}>
                            <p>
                                Download failed. Please try again later. If the problem persists, contact our technical
                                support.
                            </p>
                        </div>
                    </Fade>
                    {renderContent()}
                </Row>
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    disabled={!open || downloading}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => setOpen(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || downloading}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => _downloadFile()}
                    borderRadius={'8px 0 8px 0'}
                >
                    {downloading ? 'Downloading...' : "Download"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FilePreviewModal;
